import React from "react";
import { HelpCircle } from "lucide-react";
import { Label, SimpleSelect, Switch, Tooltip } from "@/components/ui";
import { ReviewSortType } from "@/services/bedrock";

interface Props {
  id: string;
  label: string;
  tooltip?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const SettingRow: React.FC<Props> = ({ id, label, tooltip, checked, onCheckedChange }) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3">
    <div className="flex items-center gap-1.5 min-w-0">
      <Label htmlFor={id}>{label}</Label>
      {tooltip && (
        <Tooltip>
          <Tooltip.Trigger
            render={
              <button
                type="button"
                className="inline-flex items-center text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                aria-label={`About ${label.toLowerCase()}`}
              >
                <HelpCircle className="size-3.5" />
              </button>
            }
          />
          <Tooltip.Content className="max-w-64">{tooltip}</Tooltip.Content>
        </Tooltip>
      )}
    </div>
    <Switch id={id} aria-label={label} checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

const SettingSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</Label>
    {children}
  </div>
);

const SettingGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <SettingSection title={title}>
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">{children}</div>
  </SettingSection>
);

const SortSettingRow: React.FC<{ value: ReviewSortType; onChange: (value: ReviewSortType) => void }> = ({
  value,
  onChange,
}) => (
  <div className="flex items-center justify-between gap-3 px-4 py-3">
    <Label>Sort</Label>
    <SimpleSelect
      value={value}
      onValueChange={next => onChange((next || "featured") as ReviewSortType)}
      items={[
        { label: "Featured", value: "featured" },
        { label: "Latest", value: "latest" },
      ]}
      align="end"
    />
  </div>
);

export { SettingRow, SettingSection, SettingGroup, SortSettingRow };
